import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useOrgTimezone, formatLocalDateInTz, getTodayInTz } from '../../utils/timezone';
import {
  cycleOffsetFromToday,
  cycleOffsetLabel,
  type PayPeriod,
  type PayPeriodMode,
} from '../../utils/payPeriod';

const MODE_LABELS: Record<PayPeriodMode, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  custom: 'Custom',
};

interface PayPeriodNavigatorProps {
  mode: PayPeriodMode;
  onModeChange: (mode: PayPeriodMode) => void;
  /** Segments to offer, in order. Defaults to the two real cycles. */
  modes?: PayPeriodMode[];
  /** Override a segment's wording without changing what it means. */
  modeLabels?: Partial<Record<PayPeriodMode, string>>;
  period: PayPeriod | null;
  onNavigate: (direction: 'prev' | 'next') => void;
  /**
   * Supplied only by a page that allows an arbitrary window. When present and
   * the mode is `custom`, the range becomes a pair of editable date inputs —
   * the arrows still step it by its own width.
   */
  onPeriodChange?: (period: PayPeriod) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Pay-frequency toggle plus a prev / range / next stepper.
 *
 * Shared by the pay cycle dashboard and the payroll manager so the two pages
 * cannot drift apart on what a cycle is or how you move between them.
 */
function PayPeriodNavigator({
  mode,
  onModeChange,
  modes = ['weekly', 'fortnightly'],
  modeLabels,
  period,
  onNavigate,
  onPeriodChange,
  disabled = false,
  className = '',
}: PayPeriodNavigatorProps) {
  const orgTz = useOrgTimezone();
  const editable = mode === 'custom' && !!onPeriodChange;
  const relative = period
    ? cycleOffsetLabel(cycleOffsetFromToday(period, mode, getTodayInTz(orgTz)))
    : null;

  return (
    <div className={`flex flex-col lg:flex-row lg:items-center gap-3 ${className}`}>
      {/* Frequency toggle */}
      {/*
        The group wraps rather than pushing the page sideways: three segments
        plus a filter icon do not fit 320px on one line, and a pay period
        selector that forces a horizontal scroll is worse than one on two rows.
      */}
      <div className="flex flex-wrap rounded-lg border border-neutral-200 overflow-hidden self-start max-w-full">
        {modes.map(m => (
          <button
            key={m}
            type="button"
            onClick={() => onModeChange(m)}
            aria-pressed={mode === m}
            className={`flex-1 px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              mode === m
                ? 'bg-primary-600 text-white'
                : 'bg-white text-neutral-600 hover:bg-neutral-50'
            }`}
          >
            {modeLabels?.[m] ?? MODE_LABELS[m]}
          </button>
        ))}
      </div>

      {/* Cycle stepper */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={() => onNavigate('prev')}
          disabled={disabled || !period}
          aria-label="Previous period"
          className="p-2 shrink-0 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 transition-colors disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4 text-neutral-600" />
        </button>

        <div className="flex-1 min-w-0 px-2 sm:px-4 py-1.5 bg-white border border-neutral-200 rounded-lg text-center sm:min-w-[240px]">
          {editable && period ? (
            <div className="flex items-center justify-center gap-1.5">
              <input
                type="date"
                aria-label="Period start"
                value={period.startDate}
                onChange={e => onPeriodChange!({ ...period, startDate: e.target.value })}
                className="min-w-0 w-full max-w-[9.5rem] bg-transparent text-xs sm:text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              />
              <span className="text-neutral-400 shrink-0">–</span>
              <input
                type="date"
                aria-label="Period end"
                value={period.endDate}
                onChange={e => onPeriodChange!({ ...period, endDate: e.target.value })}
                className="min-w-0 w-full max-w-[9.5rem] bg-transparent text-xs sm:text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              />
            </div>
          ) : (
            <span className="block text-xs sm:text-sm font-medium text-neutral-700 break-words">
              {period
                ? `${formatLocalDateInTz(period.startDate, orgTz)} — ${formatLocalDateInTz(period.endDate, orgTz)}`
                : 'Loading...'}
            </span>
          )}
          {relative && (
            <span className="block text-[11px] leading-tight text-neutral-400">{relative}</span>
          )}
        </div>

        <button
          type="button"
          onClick={() => onNavigate('next')}
          disabled={disabled || !period}
          aria-label="Next period"
          className="p-2 shrink-0 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 transition-colors disabled:opacity-50"
        >
          <ChevronRight className="h-4 w-4 text-neutral-600" />
        </button>
      </div>
    </div>
  );
}

export default PayPeriodNavigator;
